import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [certificates, setCertificates] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // 🔒 Protect admin route
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isAdminLoggedIn");
    if (!isLoggedIn) {
      navigate("/admin/login");
    }
  }, [navigate]);

  // 📡 Fetch certificates
  const fetchCertificates = () => {
    setLoading(true);
    fetch("https://smartgonextverify.smartgonext.com/certificates")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.certificates)) {
          setCertificates(data.certificates);
        } else {
          setCertificates([]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  // ❌ Delete certificate
  const handleDelete = async (certId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this certificate?"
    );
    if (!confirmDelete) return;

    try {
      const res = await fetch(
        `https://smartgonextverify.smartgonext.com/delete/${certId}`,
        { method: "DELETE" }
      );

      const result = await res.json();
      if (result.success) {
        fetchCertificates();
      } else {
        alert("Failed to delete certificate");
      }
    } catch {
      alert("Server error while deleting certificate");
    }
  };

  // 🔍 Search filter
  const filteredCertificates = certificates.filter((c) =>
    `${c[0]} ${c[2]} ${c[8]}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="bg-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-800">
            Certificate Management
          </h1>
          <p className="text-sm text-slate-500">
            Create, manage and verify issued certificates
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by name, email or certificate ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-96 px-4 py-2.5 rounded-xl border border-slate-300
              focus:ring-2 focus:ring-indigo-500 focus:outline-none"
          />

          <button
            onClick={() => navigate("/admin/add")}
            className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl
              hover:bg-indigo-700 transition"
          >
            + Add Certificate
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {loading ? (
            <div className="py-12 text-center text-slate-500">
              Loading certificates…
            </div>
          ) : filteredCertificates.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <p className="font-medium">No certificates found</p>
              <p className="text-sm mt-1">
                Start by adding a new certificate
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left">Cert ID</th>
                  <th className="px-4 py-3 text-left">Student</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">College</th>
                  <th className="px-4 py-3 text-left">Internship</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {filteredCertificates.map((c, index) => (
                  <tr
                    key={index}
                    className="hover:bg-slate-50 transition"
                  >
                    <td className="px-4 py-3 font-mono text-xs">
                      {c[0]}
                    </td>
                    <td className="px-4 py-3">{c[2]}</td>
                    <td className="px-4 py-3">{c[8]}</td>
                    <td className="px-4 py-3">{c[6]}</td>
                    <td className="px-4 py-3">
                      {c[10]} → {c[11]}
                    </td>
                    <td className="px-4 py-3 text-center space-x-4">
                      <button
                        onClick={() =>
                          navigate(`/admin/edit/${c[0]}`)
                        }
                        className="text-indigo-600 hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(c[0])}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
